import { Flex, Text, VStack } from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useModalState } from '~/atoms/ModalState'
import { useSettingsState } from '~/atoms/SettingsState'
import { useUserState } from '~/atoms/UserState'
import { ImageIcon } from '~/components/ImageIcon'
import { DropdownTextButton } from '~/components/shared/Dropdown/DropdownTextButton'
import { MenuItem, MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { useAnalytics } from '~/hooks/useAnalytics'
import { usePhoneEndpoint } from '~/hooks/usePhone'
import { ExtensionSelector } from '~/pages/WebPhone/ExtensionSelector'
import { AnalyticsNamespace, WebPhoneAnalyticsEvents } from '~/types/Analytics'

import { ExtensionInstance } from '@mangovoice/types'

import { DependentSectionPanel, MenuItemHeader } from './Shared'

interface PhoneSettingToggleDependentSectionProps {
  icon?: SVGAsset
  isChecked: boolean
  toggleOnContent: React.ReactNode
  toggleOffContent?: React.ReactNode
}
export const PhoneSettingToggleDependentSection = ({
  icon,
  isChecked,
  toggleOnContent,
  toggleOffContent,
}: PhoneSettingToggleDependentSectionProps) => {
  const { track } = useAnalytics(AnalyticsNamespace.WEB_PHONE)
  const { pbxExtensions, setCurrentUserExtension, pbx } = useUserState()
  const {
    confirmModal: [, setConfirmModalState],
  } = useModalState()
  const { setWebPhoneEnabled } = useSettingsState()
  const {
    state: {
      phoneCalls: {
        active: { activeCall },
      },
    },
  } = usePhoneEndpoint({})
  const handleWebPhoneActivate = () => {
    setWebPhoneEnabled(true)
  }

  //TODO put all ext stuff in separate component. shared with onboarding view
  const extItems = useMemo(() => {
    return (
      pbxExtensions?.map((ext) => {
        return {
          value: ext,
          content: (
            <MenuListItem
              primaryText={ext.name}
              rightContent={<ExtensionBadge extension={ext.number} />}
            />
          ),
          onSelect: (ext: ExtensionInstance) => {
            track(WebPhoneAnalyticsEvents.ExtSelected)
            setCurrentUserExtension(pbx?.id, ext)
            handleWebPhoneActivate()
          },
        } as MenuItem<ExtensionInstance>
      }) ?? []
    )
  }, [pbxExtensions])

  const checkCanActivatePhone = () => {
    if (pbxExtensions?.length === 1) {
      setCurrentUserExtension(pbx?.id, pbxExtensions[0])
      handleWebPhoneActivate()
    } else if (pbxExtensions?.length === 0) {
      setConfirmModalState({
        title: `No Line Extensions Available`,
        message: (
          <Flex
            sx={{
              flexDirection: 'column',
              gap: '1.5rem',
            }}
          >
            <Text sx={{ fontSize: '1rem', fontWeight: '400' }}>
              You do not have any line extensions assigned on this phone system
              ({pbx?.name}).
            </Text>
            <Text sx={{ fontSize: '1rem', fontWeight: '400' }}>
              To use the phone feature, please contact your system administrator
              to assign a line extension.
            </Text>
          </Flex>
        ),
        confirmText: 'Dismiss',
        closeAfterConfirm: true,
        isDestructive: false,
      })
    }
  }

  const shouldRoundBottomCorners = toggleOffContent === undefined && !isChecked

  return (
    <VStack gap="1rem">
      {!isChecked ? (
        <MenuItemHeader
          roundBottomCorners={shouldRoundBottomCorners}
          padding="1rem"
          height="auto"
        >
          {icon && (
            <ImageIcon width="24" height="24" marginRight="10" icon={icon} />
          )}
          <DropdownTextButton
            items={extItems.length > 1 ? extItems : []}
            placeholder="Activate Web Phone"
            onButtonClick={checkCanActivatePhone}
            menuOptions={{
              width: '23.9375rem',
            }}
            header={
              <Flex
                sx={{
                  display: 'flex',
                  padding: '0.5rem 0.75rem 0rem 0.75rem',
                  alignItems: 'center',
                  gap: '0.5rem',
                  alignSelf: 'stretch',
                }}
              >
                <Text
                  sx={{
                    color: 'gray.1300',
                    fontSize: '0.875rem',
                    fontWeight: '700',
                  }}
                >
                  Select Extension
                </Text>
              </Flex>
            }
          ></DropdownTextButton>
        </MenuItemHeader>
      ) : (
        <Flex
          sx={{
            display: 'flex',
            width: '100%',
            padding: '1rem',
            alignItems: 'center',
            gap: '0.625rem',
            border: '1.5px solid var(--Gray-Gray400, #E5E5EE)',
            background: 'gray.200',
          }}
        >
          <Text
            sx={{
              fontSize: '.875rem',
              fontWeight: '400',
              color: 'gray.1300',
              width: '100%',
            }}
          >
            Your Extension
          </Text>
          <ExtensionSelector hasActiveCall={!!activeCall} />
        </Flex>
      )}

      {isChecked && (
        <DependentSectionPanel>{toggleOnContent}</DependentSectionPanel>
      )}
      {!isChecked && toggleOffContent != undefined && (
        <DependentSectionPanel>{toggleOffContent}</DependentSectionPanel>
      )}
    </VStack>
  )
}
